import { getIsTravelApp } from "@capone/common";
import dayjs from "dayjs";
import {
  CarEntryTypeEnum,
  FlightEntryTypeEnum,
  FlightShopStep,
  HotelEntryTypeEnum,
  LodgingSelection,
  PackagesEntryTypeEnum,
  SliceStopCountFilter,
  TripCategory,
  CoreFlightsSearchQueryParams,
} from "redmond";
export const transformToStringifiedAvailabilityQuery = (
  location: string,
  fromDate: Date | string | null,
  untilDate: Date | string | null,
  adultsCount: number | null,
  children: number[] | null,
  roomsCount?: number,
  entryPoint?: HotelEntryTypeEnum
): string => {
  location = encodeURIComponent(location);
  if (fromDate === null || untilDate === null) {
    return `?locationName=${location}`;
  }

  const formatFrom =
    typeof fromDate !== "string"
      ? dayjs(fromDate).format("YYYY-MM-DD")
      : fromDate;
  const formatUntil =
    typeof untilDate !== "string"
      ? dayjs(untilDate).format("YYYY-MM-DD")
      : untilDate;

  const childrenParams = children?.reduce(
    (ageString, age) => `${ageString}&children=${age}`,
    ""
  );

  let query = `?locationName=${location}&fromDate=${formatFrom}&untilDate=${formatUntil}&adultsCount=${adultsCount}${childrenParams}`;

  if (roomsCount) {
    query += `&roomsCount=${roomsCount}`;
  }
  if (entryPoint) {
    query += `&entryPoint=${entryPoint}`;
  }
  return query;
};

export interface HotelShopQuery {
  lodgingId: string;
  fromDate: Date | string | null;
  untilDate: Date | string | null;
  adultsCount: number;
  children: number[];
  selectedAccountIndex: number;
  selectedLodgingIndex: number | null;
  lodgingSelection?: LodgingSelection;
  roomsCount?: number;
  petsCount?: number;
  fromHotelAvailability?: boolean;
  entryPoint?: HotelEntryTypeEnum;
  shopToken?: string;
}

export const transformToStringifiedQuery = ({
  lodgingId,
  fromDate,
  untilDate,
  adultsCount = 2,
  children = [],
  selectedAccountIndex = 0,
  selectedLodgingIndex,
  lodgingSelection,
  roomsCount,
  petsCount,
  fromHotelAvailability,
  entryPoint,
  shopToken,
}: HotelShopQuery): string => {
  if (fromDate === null || untilDate === null) {
    return `?lodgingId=${lodgingId}`;
  }

  const formatFrom =
    typeof fromDate !== "string"
      ? dayjs(fromDate).format("YYYY-MM-DD")
      : fromDate;
  const formatUntil =
    typeof untilDate !== "string"
      ? dayjs(untilDate).format("YYYY-MM-DD")
      : untilDate;

  const childrenParams = children?.reduce(
    (ageString, age) => `${ageString}&children=${age}`,
    ""
  );

  const lodgingSelectionParam = encodeURIComponent(
    JSON.stringify(lodgingSelection)
  );
  let string = "";
  if (roomsCount) {
    string = `?lodgingId=${lodgingId}&fromDate=${formatFrom}&untilDate=${formatUntil}&adultsCount=${adultsCount}${childrenParams}&roomsCount=${roomsCount}&selectedAccountIndex=${selectedAccountIndex}&lodgingSelection=${lodgingSelectionParam}`;
  } else {
    string = `?lodgingId=${lodgingId}&fromDate=${formatFrom}&untilDate=${formatUntil}&adultsCount=${adultsCount}${childrenParams}&selectedAccountIndex=${selectedAccountIndex}&lodgingSelection=${lodgingSelectionParam}`;
  }

  if (petsCount) {
    string += `&pets=${petsCount != 0}`;
  }

  if (selectedLodgingIndex != null) {
    string += `&selectedLodgingIndex=${selectedLodgingIndex}`;
  }

  if (fromHotelAvailability !== undefined) {
    string += `&fromHotelAvailability=${fromHotelAvailability}`;
  }
  if (entryPoint) {
    string += `&entryPoint=${entryPoint}`;
  }
  if (shopToken) {
    string += `&shopToken=${shopToken}`;
  }
  return string;
};

enum FlightShopQueryParams {
  TRIP_CATEGORY = "tripCategory",
  ORIGIN = "origin",
  DESTINATION = "destination",
  DEPARTURE_DATE = "departureDate",
  RETURN_DATE = "returnDate",
  STOPS_OPTION = "stopsOption",
  NO_LCC = "noLCC",
  FLIGHT_SHOP_PROGRESS = "flightShopProgress",
  ADULTS_COUNT = "adultsCount",
  CHILDREN_COUNT = "childrenCount",
  INFANTS_IN_SEAT_COUNT = "infantsInSeatCount",
  INFANTS_ON_LAP_COUNT = "infantsOnLapCount",
  ENTRY_POINT = "entryPoint",
  FARE_CLASS = "fareClass",
  EXPANDED_FLIGHT_CARD = "expandedFlightCard",
  OPEN_FLIGHT_DETAILS_DIALOG = "openFlightDetailsDialog",
}

interface FlightShopQuery {
  [FlightShopQueryParams.TRIP_CATEGORY]: TripCategory;
  [FlightShopQueryParams.ORIGIN]: string;
  [FlightShopQueryParams.DESTINATION]: string;
  [FlightShopQueryParams.DEPARTURE_DATE]: string;
  [FlightShopQueryParams.RETURN_DATE]?: string;
  [FlightShopQueryParams.STOPS_OPTION]?: SliceStopCountFilter;
  [FlightShopQueryParams.NO_LCC]?: boolean;
  [FlightShopQueryParams.FLIGHT_SHOP_PROGRESS]?: FlightShopStep;
  [FlightShopQueryParams.ADULTS_COUNT]: number;
  [FlightShopQueryParams.CHILDREN_COUNT]?: number;
  [FlightShopQueryParams.INFANTS_IN_SEAT_COUNT]?: number;
  [FlightShopQueryParams.INFANTS_ON_LAP_COUNT]?: number;
  [FlightShopQueryParams.ENTRY_POINT]?: FlightEntryTypeEnum;
  [FlightShopQueryParams.FARE_CLASS]?: string[];
}

export const transformToStringifiedFlightShopQuery = ({
  tripCategory,
  origin,
  destination,
  departureDate,
  returnDate,
  stopsOption = SliceStopCountFilter.ANY_NUMBER,
  noLCC = false,
  flightShopProgress = FlightShopStep.ChooseDeparture,
  adultsCount,
  childrenCount,
  infantsInSeatCount,
  infantsOnLapCount,
  entryPoint,
  fareClass,
}: FlightShopQuery) => {
  const formatDepartureDate =
    typeof departureDate !== "string"
      ? dayjs(departureDate).format("YYYY-MM-DD")
      : departureDate;
  const formatReturnDate =
    returnDate && typeof returnDate !== "string"
      ? dayjs(returnDate).format("YYYY-MM-DD")
      : returnDate;

  let string = "";
  if (adultsCount) {
    string += `?${FlightShopQueryParams.ADULTS_COUNT}=${adultsCount}`;
  }
  if (childrenCount) {
    string += `&${FlightShopQueryParams.CHILDREN_COUNT}=${childrenCount}`;
  }
  if (infantsInSeatCount) {
    string += `&${FlightShopQueryParams.INFANTS_IN_SEAT_COUNT}=${infantsInSeatCount}`;
  }
  if (infantsOnLapCount) {
    string += `&${FlightShopQueryParams.INFANTS_ON_LAP_COUNT}=${infantsOnLapCount}`;
  }
  fareClass?.forEach((fc) => {
    string += `&${FlightShopQueryParams.FARE_CLASS}=${fc}`;
  });
  if (formatDepartureDate && origin && destination) {
    string +=
      `&${FlightShopQueryParams.DEPARTURE_DATE}=${formatDepartureDate}` +
      `&${FlightShopQueryParams.DESTINATION}=${destination}` +
      `&${FlightShopQueryParams.FLIGHT_SHOP_PROGRESS}=${flightShopProgress}` +
      `&${FlightShopQueryParams.NO_LCC}=${noLCC}` +
      `&${FlightShopQueryParams.ORIGIN}=${origin}`;
  }
  if (formatReturnDate) {
    string +=
      `&${FlightShopQueryParams.RETURN_DATE}=${formatReturnDate}` +
      `&${FlightShopQueryParams.STOPS_OPTION}=${stopsOption}` +
      `&${FlightShopQueryParams.TRIP_CATEGORY}=${tripCategory}` +
      `&${FlightShopQueryParams.ENTRY_POINT}=${entryPoint}`;
  } else {
    string +=
      `&${FlightShopQueryParams.STOPS_OPTION}=${stopsOption}` +
      `&${FlightShopQueryParams.TRIP_CATEGORY}=${tripCategory}` +
      `&${FlightShopQueryParams.ENTRY_POINT}=${entryPoint}`;
  }
  return string;
};

// returns a query string for the new core-flights search params by mapping from legacy FlightShop query params.
export const getCoreFlightsSearchQueryString = (
  query: Record<string, unknown>
): string => {
  const getStr = (v: unknown): string => (v == null ? "" : String(v));
  const getNullableStr = (v: unknown): string | null =>
    v == null || v === "" ? null : String(v);
  const getNum = (v: unknown): number | null => {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };
  const getBool = (v: unknown): boolean | null => {
    if (v == null || v === "") return null;
    if (typeof v === "boolean") return v;
    const s = String(v).toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
    return null;
  };

  const params = new URLSearchParams();
  const origin = getStr(query[FlightShopQueryParams.ORIGIN]);
  const destination = getStr(query[FlightShopQueryParams.DESTINATION]);
  const departureDate = getStr(query[FlightShopQueryParams.DEPARTURE_DATE]);
  const returnDate = getNullableStr(query[FlightShopQueryParams.RETURN_DATE]);
  const tripCategory = getNullableStr(
    query[FlightShopQueryParams.TRIP_CATEGORY]
  );
  const stopsOption = getNullableStr(query[FlightShopQueryParams.STOPS_OPTION]);
  const adultCount = getNum(query[FlightShopQueryParams.ADULTS_COUNT]);
  const childCount = getNum(query[FlightShopQueryParams.CHILDREN_COUNT]);
  const infantInSeatCount = getNum(
    query[FlightShopQueryParams.INFANTS_IN_SEAT_COUNT]
  );
  const infantOnLapCount = getNum(
    query[FlightShopQueryParams.INFANTS_ON_LAP_COUNT]
  );
  const noLCC = getBool(query[FlightShopQueryParams.NO_LCC]);
  const fareClassRaw = query[FlightShopQueryParams.FARE_CLASS] as
    | string
    | string[]
    | undefined;
  const expandedFlightCard = getNullableStr(
    query[FlightShopQueryParams.EXPANDED_FLIGHT_CARD]
  );
  const openFlightDetailsDialog = getBool(
    query[FlightShopQueryParams.OPEN_FLIGHT_DETAILS_DIALOG]
  );
  const { isTravelApp } = getIsTravelApp();

  if (origin) params.set(CoreFlightsSearchQueryParams.ORIGIN, origin);
  if (destination)
    params.set(CoreFlightsSearchQueryParams.DESTINATION, destination);
  if (departureDate)
    params.set(CoreFlightsSearchQueryParams.DEPARTURE_DATE, departureDate);
  if (returnDate)
    params.set(CoreFlightsSearchQueryParams.RETURN_DATE, returnDate);
  if (tripCategory)
    params.set(CoreFlightsSearchQueryParams.TRIP_CATEGORY, tripCategory);
  if (stopsOption)
    params.set(CoreFlightsSearchQueryParams.STOPS_OPTION, stopsOption);
  if (adultCount != null)
    params.set(CoreFlightsSearchQueryParams.ADULT_COUNT, String(adultCount));
  if (childCount != null)
    params.set(CoreFlightsSearchQueryParams.CHILD_COUNT, String(childCount));
  if (infantInSeatCount != null)
    params.set(
      CoreFlightsSearchQueryParams.INFANT_IN_SEAT_COUNT,
      String(infantInSeatCount)
    );
  if (infantOnLapCount != null)
    params.set(
      CoreFlightsSearchQueryParams.INFANT_IN_LAP_COUNT,
      String(infantOnLapCount)
    );
  if (noLCC != null)
    params.set(CoreFlightsSearchQueryParams.NO_LCC, String(noLCC));

  if (fareClassRaw) {
    if (Array.isArray(fareClassRaw)) {
      fareClassRaw.forEach((fc) => {
        if (fc) params.append(CoreFlightsSearchQueryParams.FARE_CLASS, fc);
      });
    } else if (fareClassRaw) {
      params.append(CoreFlightsSearchQueryParams.FARE_CLASS, fareClassRaw);
    }
  }

  if (expandedFlightCard) {
    params.set(
      CoreFlightsSearchQueryParams.EXPANDED_FLIGHT_CARD,
      expandedFlightCard
    );
  }

  if (openFlightDetailsDialog) {
    params.set(
      CoreFlightsSearchQueryParams.OPEN_FLIGHT_DETAILS_DIALOG,
      String(openFlightDetailsDialog)
    );
  }

  if (isTravelApp) {
    params.set(CoreFlightsSearchQueryParams.IS_TRAVEL_APP, String(isTravelApp));
  }

  const qs = params.toString();
  return qs != null ? `?${qs}` : "";
};

export interface CarAvailabilityQuery {
  dropOffDate: string;
  dropOffTime: string;
  dropOffLocation: string;
  pickUpDate: string;
  pickUpTime: string;
  pickUpLocation: string;
  driverAge: number;
  entryPoint?: CarEntryTypeEnum;
}
export const transformToStringifiedCarsAvailabilityQuery = ({
  dropOffDate,
  dropOffTime,
  dropOffLocation,
  pickUpDate,
  pickUpTime,
  pickUpLocation,
  driverAge,
  entryPoint,
}: CarAvailabilityQuery) => {
  const formatDropOffDate = dayjs(dropOffDate).format("YYYY-MM-DD");
  const formatPickUpDate = dayjs(pickUpDate).format("YYYY-MM-DD");

  let string =
    `?dropOffDate=${formatDropOffDate}` +
    `&dropOffTime=${dropOffTime}` +
    `&dropOffLocation=${dropOffLocation}` +
    `&pickUpDate=${formatPickUpDate}` +
    `&pickUpTime=${pickUpTime}` +
    `&pickUpLocation=${pickUpLocation}` +
    `&driverAge=${driverAge}`;
  if (entryPoint) {
    string += `&entryPoint=${entryPoint}`;
  }
  return string;
};

export interface PackagesAvailabilityQuery {
  origin?: string | null;
  destination?: string | null;
  fromDate?: Date | string | null;
  untilDate?: Date | string | null;
  fareClass?: string;
  stopsOption: SliceStopCountFilter;
  adultsCount: number;
  childrenCount?: number;
  infantsInSeatCount?: number;
  infantsOnLapCount?: number;
  entryPoint?: PackagesEntryTypeEnum;
}

export const transformToStringifiedPackagesAvailabilityQuery = ({
  origin,
  destination,
  fromDate,
  untilDate,
  fareClass,
  stopsOption,
  adultsCount,
  childrenCount,
  infantsInSeatCount,
  infantsOnLapCount,
  entryPoint,
}: PackagesAvailabilityQuery) => {
  const queryParams = new URLSearchParams({
    origin: origin ?? "",
    destination: destination ?? "",
    stopsOption,
    adultsCount: adultsCount.toString(),
    ...(childrenCount && { childrenCount: childrenCount.toString() }),
    ...(infantsOnLapCount && {
      infantsOnLapCount: infantsOnLapCount.toString(),
    }),
    ...(infantsInSeatCount && {
      infantsInSeatCount: infantsInSeatCount.toString(),
    }),
  });

  if (fromDate) {
    const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
    queryParams.append("fromDate", formattedFromDate);
  }

  if (untilDate) {
    const formattedUntilDate = dayjs(untilDate).format("YYYY-MM-DD");
    queryParams.append("untilDate", formattedUntilDate);
  }

  if (fareClass) {
    queryParams.append("fareClass", fareClass);
  }

  if (entryPoint) {
    queryParams.append("entryPoint", entryPoint);
  }

  return `?${queryParams.toString()}`;
};
